/**
 * Quick replies service.
 *
 * Admin-managed canned message templates that show as chips above the
 * message textarea. Stored in Firestore: quickReplies/{id}.
 *
 * Public read of active replies is gated by Firestore rules.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';

export type QuickReplyVisibility = 'host' | 'guest' | 'all';

export interface QuickReply {
  id: string;
  text: string;
  visibleTo: QuickReplyVisibility;
  active: boolean;
  order: number;
  /** Grouping label shown in the mobile phrase picker (e.g. "Amenities"). */
  category?: string;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

const COLLECTION = 'quickReplies';

/** Category options for chat phrases (kept in sync with the mobile picker). */
export const CHAT_PHRASE_CATEGORIES = [
  'Greetings',
  'Availability & dates',
  'Requests',
  'Amenities',
  'Guests',
  'Arrival & check-in',
  'Booking & payment',
  'Closing',
] as const;

/**
 * Default digit-free phrase set. Used by the admin "Load default phrases"
 * seeder so the chat has good coverage out of the box.
 *
 * Every phrase carries its own `visibleTo`, because the chat is two-sided and
 * has no free-text input: a guest needs the question ("Is parking available?")
 * and the host needs an answer ("Yes, parking is available."). Seeding the
 * whole set as `all` — as this list used to — put guest-only questions in the
 * host's picker and left hosts with no way to reply.
 */
export const DEFAULT_CHAT_PHRASES: Array<{
  text: string;
  category: string;
  visibleTo: QuickReplyVisibility;
}> = [
  // ---------- GUEST -> HOST ----------
  { visibleTo: 'guest', category: 'Greetings', text: 'Hi! Is this place available for my dates?' },
  { visibleTo: 'guest', category: 'Greetings', text: 'Hello! I am interested in booking your place.' },
  { visibleTo: 'guest', category: 'Greetings', text: 'Thanks for the quick reply!' },
  { visibleTo: 'guest', category: 'Greetings', text: 'Sounds good, thank you!' },
  { visibleTo: 'guest', category: 'Greetings', text: 'Looking forward to it!' },

  { visibleTo: 'guest', category: 'Availability & dates', text: 'Are these dates still available?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'Is the place available for a longer stay?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'Could I extend my stay by a few nights?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'Can I check in earlier?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'Is a late check-out possible?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'What time is check-in?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'What time is check-out?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'Is the place free next weekend?' },
  { visibleTo: 'guest', category: 'Availability & dates', text: 'Do you have availability during the holidays?' },

  { visibleTo: 'guest', category: 'Requests', text: 'Can we add one more mattress?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Could we get extra pillows?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Do you provide extra blankets?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Could you provide extra towels?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Can you provide a baby crib?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Could you provide a high chair?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Is early check-in possible?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Could you turn on the air conditioning before we arrive?' },
  { visibleTo: 'guest', category: 'Requests', text: 'Can we store our luggage after check-out?' },

  { visibleTo: 'guest', category: 'Amenities', text: 'Is parking available?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is Wi-Fi available?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there heating and air conditioning?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is the kitchen fully equipped?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is the place pet-friendly?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there hot water?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Do you have a pool?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there a generator or backup power?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there a washing machine?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Do you have accessibility for wheelchairs?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there an elevator in the building?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is smoking allowed?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there a sea view?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Is there a barbecue area?' },
  { visibleTo: 'guest', category: 'Amenities', text: 'Are bed linens and towels provided?' },

  { visibleTo: 'guest', category: 'Guests', text: 'We will be a small family with kids.' },
  { visibleTo: 'guest', category: 'Guests', text: 'We are a couple travelling together.' },
  { visibleTo: 'guest', category: 'Guests', text: 'Can the place host a few extra guests?' },
  { visibleTo: 'guest', category: 'Guests', text: 'Is the place suitable for children?' },
  { visibleTo: 'guest', category: 'Guests', text: 'Are we allowed to have visitors?' },
  { visibleTo: 'guest', category: 'Guests', text: 'Can we bring our dog?' },

  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'How do we get the keys?' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'Is self check-in available?' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'Could you share directions to the place?' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'Where exactly should we park?' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'Will someone meet us on arrival?' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'We are on our way.' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'We have arrived.' },
  { visibleTo: 'guest', category: 'Arrival & check-in', text: 'We will arrive later than planned.' },

  { visibleTo: 'guest', category: 'Booking & payment', text: 'I will book through the app.' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'How does payment work?' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'Is the price negotiable for a longer stay?' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'Please confirm my booking request.' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'Are there any extra fees?' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'Is a deposit required?' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'Does the price include cleaning?' },
  { visibleTo: 'guest', category: 'Booking & payment', text: 'What is your cancellation policy?' },

  { visibleTo: 'guest', category: 'Closing', text: 'Thank you so much!' },
  { visibleTo: 'guest', category: 'Closing', text: 'Appreciate your help!' },
  { visibleTo: 'guest', category: 'Closing', text: 'Thank you for hosting us!' },
  { visibleTo: 'guest', category: 'Closing', text: 'We really enjoyed our stay.' },

  // ---------- HOST -> GUEST ----------
  { visibleTo: 'host', category: 'Greetings', text: 'Hello! Thank you for your interest.' },
  { visibleTo: 'host', category: 'Greetings', text: 'Hi! Happy to help.' },
  { visibleTo: 'host', category: 'Greetings', text: 'Thanks for reaching out!' },
  { visibleTo: 'host', category: 'Greetings', text: 'Welcome!' },

  { visibleTo: 'host', category: 'Availability & dates', text: 'Yes, those dates are available.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'Sorry, those dates are already booked.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'The place is available for a longer stay.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'Early check-in is possible.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'Sorry, early check-in is not possible that day.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'Late check-out is possible.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'Sorry, late check-out is not possible.' },
  { visibleTo: 'host', category: 'Availability & dates', text: 'Please confirm your exact dates.' },

  { visibleTo: 'host', category: 'Requests', text: 'Yes, we can arrange that.' },
  { visibleTo: 'host', category: 'Requests', text: 'Sorry, that is not possible.' },
  { visibleTo: 'host', category: 'Requests', text: 'We can add an extra mattress.' },
  { visibleTo: 'host', category: 'Requests', text: 'Extra blankets are available on request.' },
  { visibleTo: 'host', category: 'Requests', text: 'Extra towels will be ready for you.' },
  { visibleTo: 'host', category: 'Requests', text: 'We can provide a baby crib.' },
  { visibleTo: 'host', category: 'Requests', text: 'Luggage storage after check-out is fine.' },

  { visibleTo: 'host', category: 'Amenities', text: 'Yes, parking is available.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Sorry, there is no parking on site.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Wi-Fi is available.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Heating and air conditioning are available.' },
  { visibleTo: 'host', category: 'Amenities', text: 'The kitchen is fully equipped.' },
  { visibleTo: 'host', category: 'Amenities', text: 'The place is pet-friendly.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Sorry, pets are not allowed.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Hot water is available.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Yes, there is a pool.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Sorry, there is no pool.' },
  { visibleTo: 'host', category: 'Amenities', text: 'There is backup power.' },
  { visibleTo: 'host', category: 'Amenities', text: 'A washing machine is available.' },
  { visibleTo: 'host', category: 'Amenities', text: 'The place is wheelchair accessible.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Sorry, the place is not wheelchair accessible.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Smoking is not allowed indoors.' },
  { visibleTo: 'host', category: 'Amenities', text: 'Bed linens and towels are provided.' },

  { visibleTo: 'host', category: 'Guests', text: 'The place is suitable for families with children.' },
  { visibleTo: 'host', category: 'Guests', text: 'We can host extra guests.' },
  { visibleTo: 'host', category: 'Guests', text: 'Sorry, we cannot host extra guests.' },
  { visibleTo: 'host', category: 'Guests', text: 'Please tell me how many guests are coming.' },
  { visibleTo: 'host', category: 'Guests', text: 'Visitors are welcome.' },
  { visibleTo: 'host', category: 'Guests', text: 'Sorry, visitors are not allowed.' },

  { visibleTo: 'host', category: 'Arrival & check-in', text: 'I will meet you at the entrance.' },
  { visibleTo: 'host', category: 'Arrival & check-in', text: 'Self check-in is available.' },
  { visibleTo: 'host', category: 'Arrival & check-in', text: 'I will share directions in the app.' },
  { visibleTo: 'host', category: 'Arrival & check-in', text: 'You can park in front of the building.' },
  { visibleTo: 'host', category: 'Arrival & check-in', text: 'The keys will be handed over on arrival.' },
  { visibleTo: 'host', category: 'Arrival & check-in', text: 'Please message me when you are on your way.' },
  { visibleTo: 'host', category: 'Arrival & check-in', text: 'Please message me when you arrive.' },

  { visibleTo: 'host', category: 'Booking & payment', text: 'Please book through the app.' },
  { visibleTo: 'host', category: 'Booking & payment', text: 'I have confirmed your booking.' },
  { visibleTo: 'host', category: 'Booking & payment', text: 'Payment is handled through the app.' },
  { visibleTo: 'host', category: 'Booking & payment', text: 'A discount is possible for a longer stay.' },
  { visibleTo: 'host', category: 'Booking & payment', text: 'Sorry, the price is fixed.' },
  { visibleTo: 'host', category: 'Booking & payment', text: 'There are no extra fees.' },
  { visibleTo: 'host', category: 'Booking & payment', text: 'Cleaning is included in the price.' },

  { visibleTo: 'host', category: 'Closing', text: 'Thank you for booking!' },
  { visibleTo: 'host', category: 'Closing', text: 'You are most welcome!' },
  { visibleTo: 'host', category: 'Closing', text: 'Have a safe trip!' },
  { visibleTo: 'host', category: 'Closing', text: 'Enjoy your stay!' },
  { visibleTo: 'host', category: 'Closing', text: 'Hope to host you again!' },

  // ---------- BOTH SIDES ----------
  { visibleTo: 'all', category: 'Greetings', text: 'Yes.' },
  { visibleTo: 'all', category: 'Greetings', text: 'No.' },
  { visibleTo: 'all', category: 'Greetings', text: 'Maybe, let me check.' },
  { visibleTo: 'all', category: 'Greetings', text: 'Okay, noted.' },
  { visibleTo: 'all', category: 'Greetings', text: 'Could you repeat that?' },
  { visibleTo: 'all', category: 'Greetings', text: 'I will get back to you shortly.' },
  { visibleTo: 'all', category: 'Closing', text: 'Thanks!' },
  { visibleTo: 'all', category: 'Closing', text: 'Sorry for the late reply.' },
];

const toDate = (v: unknown): Date | undefined => {
  if (v instanceof Timestamp) return v.toDate();
  if (v instanceof Date) return v;
  return undefined;
};

/**
 * Load active quick replies visible to the given role.
 * Defaults to fetching ALL active replies (caller filters by role).
 */
export async function fetchActiveQuickReplies(): Promise<QuickReply[]> {
  const q = query(collection(db, COLLECTION), where('active', '==', true), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      text: (data.text as string) || '',
      visibleTo: ((data.visibleTo as QuickReplyVisibility) || 'all'),
      active: data.active === true,
      order: typeof data.order === 'number' ? data.order : 0,
      category: (data.category as string) || undefined,
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
      createdBy: data.createdBy as string | undefined,
    };
  });
}

/**
 * React hook: returns active quick replies filtered by viewer role.
 */
export function useQuickReplies(viewerRole: 'host' | 'guest'): {
  replies: QuickReply[];
  isLoading: boolean;
} {
  const [replies, setReplies] = useState<QuickReply[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setIsLoading(true);
    fetchActiveQuickReplies()
      .then((all) => {
        if (!alive) return;
        setReplies(
          all.filter((r) => r.visibleTo === 'all' || r.visibleTo === viewerRole)
        );
      })
      .catch((err) => console.warn('Failed to load quick replies:', err))
      .finally(() => {
        if (alive) setIsLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [viewerRole]);

  return { replies, isLoading };
}

// --------------- admin CRUD ---------------

export async function fetchAllQuickReplies(): Promise<QuickReply[]> {
  const q = query(collection(db, COLLECTION), orderBy('order', 'asc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => {
    const data = d.data();
    return {
      id: d.id,
      text: (data.text as string) || '',
      visibleTo: ((data.visibleTo as QuickReplyVisibility) || 'all'),
      active: data.active === true,
      order: typeof data.order === 'number' ? data.order : 0,
      category: (data.category as string) || undefined,
      createdAt: toDate(data.createdAt),
      updatedAt: toDate(data.updatedAt),
      createdBy: data.createdBy as string | undefined,
    };
  });
}

export async function createQuickReply(
  data: Pick<QuickReply, 'text' | 'visibleTo' | 'order' | 'active'> &
    Partial<Pick<QuickReply, 'category'>>,
  createdBy: string
): Promise<string> {
  const ref = await addDoc(collection(db, COLLECTION), {
    text: data.text,
    visibleTo: data.visibleTo,
    order: data.order,
    active: data.active,
    category: data.category ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  return ref.id;
}

export async function updateQuickReply(
  id: string,
  data: Partial<Pick<QuickReply, 'text' | 'visibleTo' | 'order' | 'active' | 'category'>>
): Promise<void> {
  await updateDoc(doc(db, COLLECTION, id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteQuickReply(id: string): Promise<void> {
  await deleteDoc(doc(db, COLLECTION, id));
}

/**
 * Bulk-create the default phrase set. Ordering is assigned per category so
 * phrases group cleanly in the picker. Returns the number created.
 */
export async function seedDefaultChatPhrases(createdBy: string): Promise<number> {
  let created = 0;
  for (let i = 0; i < DEFAULT_CHAT_PHRASES.length; i++) {
    const p = DEFAULT_CHAT_PHRASES[i];
    await addDoc(collection(db, COLLECTION), {
      text: p.text,
      visibleTo: p.visibleTo,
      order: (i + 1) * 10,
      active: true,
      category: p.category,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      createdBy,
    });
    created++;
  }
  return created;
}
